import { createSlice } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
const backendUrl = process.env.REACT_APP_BACKEND_URL;

const initialState = {
  isLoading: false,
  error: null,
  purchaseOrders: [],
  purchaseOrder: null,
  warehouses: [],
};

const slice = createSlice({
  name: "purchaseOrder",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getPurchaseOrdersSuccess(state, action) {
      state.isLoading = false;
      state.purchaseOrders = action.payload;
    },
    getPurchaseOrderSuccess(state, action) {
      state.isLoading = false;
      state.purchaseOrder = action.payload;
    },
    getWareHouseSuccess(state, action) {
      state.isLoading = false;
      state.warehouses = action.payload;
    },
    resetStockStore() {
      return initialState;
    },

    updatePurchaseOrderStatus(state, action) {
      const { id, newStatus, rejectionReason } = action.payload;
      const orderIndex = state.purchaseOrders.findIndex(
        (order) => order.id === id
      );
      if (orderIndex !== -1) {
        state.purchaseOrders[orderIndex].purchaseOrderState = newStatus;
        if (rejectionReason) {
          state.purchaseOrders[orderIndex].rejectionReason = rejectionReason;
        }
      }
      if (state.purchaseOrder?.id === id) {
        state.purchaseOrder.purchaseOrderState = newStatus;
        if (rejectionReason) {
          state.purchaseOrder.rejectionReason = rejectionReason;
        }
      }
    },
  },
});

export default slice.reducer;

export function getPurchaseOrders(
  warehouseId,
  startDate = null,
  endDate = null
) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const params = {
        warehouseId,
        isDeleted: false,
        isActivePurchaseOrder: false,
        limit: 100000000,
      };

      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }

      const response = await axios.get(`${backendUrl}/purchaseOrder`, {
        params,
      });

      // Filter out drafted orders here
      const filteredData = response.data.results.filter(
        (order) => order.purchaseOrderState !== "drafted"
      );

      dispatch(slice.actions.getPurchaseOrdersSuccess(filteredData));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getWareHouses(franchiseId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `${backendUrl}/warehouse/getWarehouseListByFranchise/${franchiseId}?isDeleted=false`
      );
      dispatch(slice.actions.getWareHouseSuccess(response.data.results));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPurchaseOrder(adjustmentId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `${backendUrl}/purchaseOrder/${adjustmentId}`
      );
      dispatch(slice.actions.getPurchaseOrderSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getActiveInventoryPurchaseOrder(venueId, levelId, venueUserId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `${backendUrl}/purchaseOrder/activePurchaseOrder`,
        {
          params: {
            venueId,
            levelId,
            venueUserId: venueUserId,
          },
        }
      );
      dispatch(slice.actions.getPurchaseOrderSuccess(response.data));
      return { data: response?.data, status: response.status };
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function addIngredientInPurchaseOrder(data, ingredientId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.patch(
        `${backendUrl}/purchaseOrder/addIngredientInPurchaseOrder/${ingredientId}`,
        data
      );
      dispatch(slice.actions.getPurchaseOrderSuccess(response.data));
      return { data: response?.data, status: response.status };
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function deleteIngredientFromPurchaseOrder(data, ingredientId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.patch(
        `${backendUrl}/purchaseOrder/deleteIngredientFromPurchaseOrder/${ingredientId}`,
        data
      );
      dispatch(slice.actions.getPurchaseOrderSuccess(response.data));
      return { data: response?.data, status: response.status };
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function removeDraftedIngredientsFromAdjustment(deletedId, deletedById) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.delete(
        `${backendUrl}/purchaseOrder/${deletedId}`,
        {
          params: {
            deletedById,
            deletedBy: "venueUser",
          },
        }
      );
      dispatch(slice.actions.getPurchaseOrdersSuccess(response.data));
      return { data: response?.data, status: response.status };
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function cleaPurchaseOrderAfterUpdating() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.resetStockStore());
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export const { updatePurchaseOrderStatus } = slice.actions;
